<template>
    <div class="lg:grid lg:grid-cols-12 lg:gap-x-5">
        <ControlsMenuComponent class="sm:mb-6" />

        <div class="w-full grid grid-cols-1 col-span-12 mt-3 sm:mt-0">
            <div class="w-full sm:max-w-4xl flex flex-col mx-auto">
                <div class="rounded-lg bg-white shadow-sm col-span-2 sm:col-span-1 mb-4">
                    <div class="p-4">
                        <div class="sm:flex sm:items-center">
                            <div class="sm:flex-auto">
                                <h1 class="text-base font-semibold text-gray-900">{{ $t('favorites.title') }}</h1>
                                <p class="mt-1 text-sm text-gray-700">{{ $t('favorites.description') }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="p-5 min-h-56 flex flex-col justify-center items-center text-center" v-if="favorites.length === 0">
                    <svg class="w-48 mx-auto mb-4" width="178" height="90" viewBox="0 0 178 90" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="27" y="50.5" width="124" height="39" rx="7.5" fill="currentColor" class="fill-white"/>
                        <rect x="27" y="50.5" width="124" height="39" rx="7.5" stroke="currentColor" class="stroke-gray-50"/>
                        <rect x="34.5" y="58" width="24" height="24" rx="4" fill="currentColor" class="fill-gray-50"/>
                        <rect x="66.5" y="61" width="60" height="6" rx="3" fill="currentColor" class="fill-gray-50"/>
                        <rect x="66.5" y="73" width="77" height="6" rx="3" fill="currentColor" class="fill-gray-50"/>
                        <rect x="19.5" y="28.5" width="139" height="39" rx="7.5" fill="currentColor" class="fill-white"/>
                        <rect x="19.5" y="28.5" width="139" height="39" rx="7.5" stroke="currentColor" class="stroke-gray-100"/>
                        <rect x="27" y="36" width="24" height="24" rx="4" fill="currentColor" class="fill-gray-100"/>
                        <rect x="59" y="39" width="60" height="6" rx="3" fill="currentColor" class="fill-gray-100"/>
                        <rect x="59" y="51" width="92" height="6" rx="3" fill="currentColor" class="fill-gray-100"/>
                        <g filter="url(#filter19)">
                            <rect x="12" y="6" width="154" height="40" rx="8" fill="currentColor" class="fill-white" shape-rendering="crispEdges"/>
                            <rect x="12.5" y="6.5" width="153" height="39" rx="7.5" stroke="currentColor" class="stroke-gray-100" shape-rendering="crispEdges"/>
                            <rect x="20" y="14" width="24" height="24" rx="4" fill="currentColor" class="fill-gray-200 "/>
                            <rect x="52" y="17" width="60" height="6" rx="3" fill="currentColor" class="fill-gray-200"/>
                            <rect x="52" y="29" width="106" height="6" rx="3" fill="currentColor" class="fill-gray-200"/>
                        </g>
                        <defs>
                            <filter id="filter19" x="0" y="0" width="178" height="64" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                <feOffset dy="6"/>
                                <feGaussianBlur stdDeviation="6"/>
                                <feComposite in2="hardAlpha" operator="out"/>
                                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.03 0"/>
                                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1187_14810"/>
                                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1187_14810" result="shape"/>
                            </filter>
                        </defs>
                    </svg>

                    <div class="max-w-full mx-auto">
                        <p class="mt-2 font-medium text-gray-800">
                            {{ $t('favorites.empty') }}
                        </p>
                        <p class="mb-5 text-sm text-gray-500">
                            {{ $t('favorites.empty_ask') }}
                        </p>
                    </div>
                </div>

                <div class="flex flex-col" v-else>
                    <div v-for="(favorite, index) in favorites" :key="`favorite-item-${favorite.id}-${index}`" class="relative group p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 bg-white border border-gray-200 -mt-px first:mt-0 first:rounded-t-xl last:rounded-b-xl">
                        <div class="grid grid-cols-3 gap-1">
                            <a v-for="item in favorite.latest"
                                :href="favorite.public_url" target="_blank"
                                :key="`favorite-item-${favorite.id}-${item.id}`"
                                class="group relative block sm:w-20 h-24 sm:h-16 flex flex-col justify-center items-center bg-gray-100 border border-gray-200 rounded-lg overflow-hidden after:absolute after:inset-0 hover:after:bg-gray-900/50 after:duration-300 focus:outline-hidden focus:after:bg-gray-900/70">
                                <img :src="item.preview_mini_url" class="object-cover w-full h-full" :alt="favorite.title" />
                            </a>
                            <a :href="favorite.public_url" target="_blank" v-if="favorite.latest.length == 0" class="group relative block col-span-3 sm:w-62 h-24 sm:h-16 flex flex-col justify-center items-center p-3 bg-gray-100 border border-gray-200 first:rounded-s-lg last:rounded-e-lg overflow-hidden after:absolute after:inset-0 after:duration-300 focus:outline-hidden">
                                <span class="mx-auto text-gray-500 text-sm">{{ $t('favorites.latest_empty') }}</span>
                            </a>
                        </div>

                        <div class="grow">
                            <div class="flex justify-between">
                                <div>
                                    <p class="text-sm font-medium text-gray-800 inline-flex items-center gap-2">
                                        <a class="hover:text-royal-blue-600 focus:outline-hidden focus:text-royal-blue-600" :href="favorite.public_url" target="_blank">
                                            {{ favorite.title }}
                                        </a>

                                        <span v-if="favorite.is_finished" class="inline-flex items-center gap-x-1.5 rounded-md px-1.5 py-0.5 text-xs font-medium text-gray-900 ring-1 ring-gray-200 ring-inset">
                                        <svg class="size-1.5 fill-green-500" viewBox="0 0 6 6" aria-hidden="true">
                                            <circle cx="3" cy="3" r="3" />
                                        </svg>
                                        {{ $t('favorites.finished') }}
                                    </span>
                                    </p>
                                    <ul class="mt-1 text-xs text-gray-500">
                                        <li class="inline-block relative pe-3 last:pe-0 first-of-type:before:hidden before:absolute before:top-1/2 before:-start-2 before:-translate-y-1/2 before:w-px before:h-3 before:bg-gray-300 before:rounded-full">
                                            {{ $t('favorites.updated_at') }} {{ favorite.updated_at_formatted }}
                                        </li>
                                        <li class="inline-block relative pe-3 last:pe-0 first-of-type:before:hidden before:absolute before:top-1/2 before:-start-2 before:-translate-y-1/2 before:w-px before:h-3 before:bg-gray-300 before:rounded-full">
                                            {{ favorite.items_count }} {{ $t('favorites.photos') }}
                                        </li>
                                    </ul>
                                </div>

                                <!-- Desktop Buttons -->
                                <div>
                                    <div class="hidden sm:flex gap-2">
                                        <button
                                            @click.prevent="toggleLightroom(favorite)"
                                            type="button"
                                            class="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm rounded-lg border border-transparent text-gray-500 hover:bg-royal-blue-100/50 focus:outline-hidden focus:bg-royal-blue-100 hover:text-gray-700 focus:outline-hidden focus:bg-royal-blue-100 focus:text-gray-500 disabled:opacity-50 disabled:pointer-events-none cursor-pointer"
                                            style="--tw-ring-color: rgb(1 30 54)">
                                            <span class="hidden sm:block">{{ $t('favorites.copy') }}</span>
                                            <span class="size-4">
                                        <img src="@/assets/images/icons/lightroom.svg" class="size-4" :alt="$t('favorites.copy_to_lightroom')">
                                    </span>
                                        </button>
                                        <button
                                            @click.prevent="toggleCaptureOne(favorite)"
                                            type="button"
                                            class="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm rounded-lg border border-transparent text-royal-blue-500 hover:bg-royal-blue-100/50 focus:outline-hidden focus:bg-royal-blue-100 hover:text-royal-blue-600 focus:outline-hidden focus:bg-royal-blue-100 focus:text-royal-blue-500 disabled:opacity-50 disabled:pointer-events-none cursor-pointer">
                                    <span class="size-4">
                                        <img src="@/assets/images/icons/captureone.svg" class="size-4" :alt="$t('favorites.copy_to_capture_one')">
                                    </span>
                                        </button>
                                        <button
                                            @click.prevent="remove(favorite)"
                                            type="button"
                                            class="ml-2 bg-red-500 text-white p-2 px-3 rounded-md text-sm hover:bg-red-600 focus:outline-none cursor-pointer flex items-center gap-1">
                                            <TrashIcon class="size-4" />
                                        </button>
                                    </div>
                                </div>

                                <!-- Mobile Dropdown -->
                                <div class="sm:hidden">
                                    <DropdownMenu
                                        :options="dropdownOptions"
                                        trigger-text="More"
                                        @action="(action, option) => handleDropdownAction(favorite, action)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <LightroomModalComponent :open="lightRoom !== null" @close="lightRoom = null" />
        <CaptureOneModalComponent :open="captureOne !== null" @close="captureOne = null" />
        <DeleteConfirmationModalComponent
            :open="deleteConfirmation !== null"
            @close="deleteConfirmation = null"
            @confirm-action="confirmRemove(deleteConfirmation!)"
        />
    </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";

import ControlsMenuComponent from "@/components/Galleries/ControlsMenuComponent.vue";
import DropdownMenu from "@/components/UI/DropdownMenu.vue";

import {Favorite} from "@/types/GalleryType";
import {deleteFavorite} from "@/data/requests/gallery";
import LightroomModalComponent from "@/components/Favorites/LightroomModalComponent.vue";

import { TrashIcon } from '@heroicons/vue/24/outline';
import DeleteConfirmationModalComponent from "@/components/Favorites/DeleteConfirmationModalComponent.vue";
import CaptureOneModalComponent from "@/components/Favorites/CaptureOneModalComponent.vue";
import type { DropdownOption } from "@/types/DropdownType";

export default defineComponent({
    name: "FavoritesPage",

    components: {
        CaptureOneModalComponent,
        DeleteConfirmationModalComponent,
        LightroomModalComponent,
        ControlsMenuComponent,
        DropdownMenu,

        TrashIcon
    },

    data() {
        return {
            lightRoom: null as Favorite | null,
            captureOne: null as Favorite | null,

            deleteConfirmation: null as Favorite | null,
        }
    },

    computed: {
        ...mapGetters({
            gallery: 'galleries/gallery',
            favorites: 'galleries/favorites'
        }),

        currentSlug(): string {
            return this.$route.params.gallery as string;
        },

        isCurrentGallery(): boolean {
            return this.gallery != null && this.gallery.slug == this.currentSlug;
        },

        dropdownOptions(): DropdownOption[] {
            return [
                {
                    id: 'lightroom',
                    label: this.$t('favorites.copy_to_lightroom'),
                    icon: 'lightroom',
                    action: 'lightroom'
                },
                {
                    id: 'captureone',
                    label: this.$t('favorites.copy_to_capture_one'),
                    icon: 'captureone',
                    action: 'captureone'
                },
                {
                    id: 'delete',
                    label: this.$t('favorites.delete'),
                    icon: 'delete',
                    action: 'delete',
                    variant: 'danger'
                }
            ];
        }
    },

    mounted() {
        if(this.isCurrentGallery) {
            this.fetchFavorites();

            return;
        }

        this.fetchGallery();
    },

    methods: {
        ...mapActions({
            loadGallery: "galleries/loadGallery",
            loadFavorites: "galleries/loadFavorites",

            errorAlert: "alerts/error",
            successAlert: "alerts/success",
        }),

        fetchGallery() {
            this.loadGallery({slug: this.currentSlug})
                .then(() => {
                    this.fetchFavorites();
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: `Під час завантаження інформації про галерею виникла помилка (${data.message})`,
                    });
                });
        },

        fetchFavorites() {
            this.loadFavorites({gallery: this.currentSlug})
                .then(() => {
                    //
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: `Під час завантаження списку обраних виникла помилка (${data.message})`,
                    });
                });
        },

        toggleLightroom(favorite: Favorite) {
            if(this.lightRoom === null) {
                this.lightRoom = favorite;

                navigator.clipboard.writeText(favorite.lightroom_list.join(' '));

                return;
            }

            if(this.lightRoom.id == favorite.id) {
                this.lightRoom = null;

                return;
            }

            this.lightRoom = favorite;

            navigator.clipboard.writeText(favorite.lightroom_list.join(' '));
        },

        toggleCaptureOne(favorite: Favorite) {
            if(this.captureOne === null) {
                this.captureOne = favorite;

                navigator.clipboard.writeText(favorite.lightroom_list.join(' '));

                return;
            }

            if(this.captureOne.id == favorite.id) {
                this.captureOne = null;

                return;
            }

            this.captureOne = favorite;

            navigator.clipboard.writeText(favorite.lightroom_list.join(' '));
        },

        confirmRemove(favorite: Favorite) {
            this.deleteConfirmation = null;

            if(!favorite) {
                return;
            }

            deleteFavorite(this.currentSlug, favorite.id, () => {
                this.fetchFavorites();

                this.successAlert({
                    message: `Список обраних "${favorite.title}" успішно видалено`,
                });
            }, (type, data) => {
                this.errorAlert({
                    message: `Під час видалення списку обраних виникла помилка (${data.message})`,
                });
            });
        },

        remove(favorite: Favorite) {
            this.deleteConfirmation = favorite;
        },

        handleDropdownAction(favorite: Favorite, action: string) {
            switch (action) {
                case 'lightroom':
                    this.toggleLightroom(favorite);
                    break;
                case 'captureone':
                    this.toggleCaptureOne(favorite);
                    break;
                case 'view':
                    this.viewPhotos(favorite);
                    break;
                case 'delete':
                    this.remove(favorite);
                    break;
            }
        },

        viewPhotos(favorite: Favorite) {
            // Відкриваємо посилання в новій вкладці
            window.open(favorite.public_url, '_blank');
        },

        handleStaticDropdownAction(action: string) {
            switch (action) {
                case 'lightroom':
                    console.log('Lightroom static item');
                    break;
                case 'captureone':
                    console.log('Capture One static item');
                    break;
                case 'view':
                    console.log('View static item');
                    break;
                case 'delete':
                    console.log('Delete static item');
                    break;
            }
        },
    }
});
</script>

<style scoped>

</style>